import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchStoreLocations } from "../api/stores";

const PgStoresContext = createContext({
  storesWithAll: ["All Stores"],
  storeNames: [],
  storeMap: {},
  storeRecords: [],
  brands: [],
  loaded: false,
});

export function usePgStores() {
  return useContext(PgStoresContext);
}

const PG_STORE_LIST_POLL_MS = 3 * 60 * 1000;

function parseStorePayload(data) {
  const rows = data?.stores;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const map = {};
  const records = [];
  const names = [];
  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const name = String(row.location_name || "").trim();
    if (!name) return;
    names.push(name);
    const rawId = row.unified_location_id;
    let locId = null;
    if (rawId != null && rawId !== "") {
      const num = Number(rawId);
      if (Number.isFinite(num)) {
        locId = num;
        map[name] = num;
      }
    }
    const brandRaw = row.brand_id;
    let brandId = null;
    if (brandRaw != null && brandRaw !== "") {
      const n = Number(brandRaw);
      if (Number.isFinite(n)) brandId = n;
    }
    records.push({
      location_name: name,
      unified_location_id: locId,
      location_type: String(row.location_type || "STORE").toUpperCase(),
      brand_id: brandId,
      brand_key: row.brand_key ? String(row.brand_key).trim() : null,
      brand_name: row.brand_name ? String(row.brand_name).trim() : null,
      store_operating_model: row.store_operating_model || null,
    });
  });
  const uniqueNames = [...new Set(names)].sort((a, b) => a.localeCompare(b));
  if (!uniqueNames.length) return null;
  const catalogBrands = Array.isArray(data?.brands)
    ? data.brands
        .map((b) => ({
          id: Number(b.id),
          key: String(b.key || "").trim(),
          name: String(b.name || "").trim(),
        }))
        .filter((b) => Number.isFinite(b.id) && b.key)
    : [];
  return {
    storesWithAll: ["All Stores", ...uniqueNames],
    storeMap: map,
    storeRecords: records,
    brands: catalogBrands,
  };
}

export default function StoresProvider({ user, children }) {
  const [storesWithAll, setStoresWithAll] = useState(["All Stores"]);
  const [storeMap, setStoreMap] = useState({});
  const [storeRecords, setStoreRecords] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const storeNames = useMemo(
    () => storesWithAll.filter((s) => s !== "All Stores"),
    [storesWithAll]
  );

  const loadStores = useCallback(() => {
    fetchStoreLocations()
      .then((data) => {
        const parsed = parseStorePayload(data);
        if (!parsed) return;
        setStoresWithAll(parsed.storesWithAll);
        setStoreMap(parsed.storeMap);
        setStoreRecords(parsed.storeRecords);
        setBrands(parsed.brands);
        setLoaded(true);
      })
      .catch((err) => {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[stores] fetch error:", err);
        }
      });
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadStores();
    const id = setInterval(loadStores, PG_STORE_LIST_POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") loadStores();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // Reload catalog on login/logout, not every user object identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user?.id is the intended dep
  }, [user?.id, loadStores]);

  const value = useMemo(
    () => ({
      storesWithAll,
      storeNames,
      storeMap,
      storeRecords,
      brands,
      loaded,
    }),
    [storesWithAll, storeNames, storeMap, storeRecords, brands, loaded]
  );

  return <PgStoresContext.Provider value={value}>{children}</PgStoresContext.Provider>;
}
